import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { finalize, first, switchMap, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { Package } from '../../models/package';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { SiteService } from '../../services/site.service';

import { AccountPackageComponent } from '../account-package/account-package.component';

/**
 * Packages tab — list view + CRUD operations for the account's catalogue tiers.
 *
 * Mirrors the AccountCharityListComponent pattern for the table/persistence
 * loop, but the per-item editor is dedicated (AccountPackageComponent)
 * because packages have multi-field bodies and nested items collections
 * that the generic inputNameValuePair modal can't cover.
 *
 * Storage: account.data.packages is an id-keyed object collection (matches
 * charities, eventTypes, etc.). Save is the existing accountService.patch
 * — no special endpoint needed.
 */
@Component({
  selector: 'app-account-package-list',
  templateUrl: './account-package-list.component.html',
  styleUrls: ['./account-package-list.component.scss'],
})
export class AccountPackageListComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  account$: Observable<Account>;

  constructor(
    private siteService: SiteService,
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Helper for the table — accepts `account.data.packages` and returns
   * the items as a stable sorted array. Soft-deleted items are kept so
   * authors can restore them.
   *
   * Defensive on shape because account.data is normalised client-side:
   * the same Conductor `account_data` EAV that the server stores as an
   * id-keyed object becomes an array on the client side
   * (objectsToArrays). Other places in the codebase might also write
   * to it directly, so handle both shapes.
   */
  packagesAsList(data: { [id: string]: Package } | Package[] | undefined): Package[] {
    if (!data) return [];
    const list = Array.isArray(data)
      ? data.filter(Boolean)
      : Object.values(data).filter(Boolean);
    return _.sortBy(list, (p) => p?._meta?.index ?? Number.MAX_SAFE_INTEGER);
  }

  onAddPackage(event: Event): void {
    // Generate a fresh id and seed the new package with default _meta
    // matching the convention (id-prefix + short suffix, integer index).
    const id = `id-pkg-${this.shortId()}`;
    const account = this.lastAccountSnapshot();
    if (!account) return;
    const existing = this.packagesAsList(account.data?.packages);
    const nextIndex = this.computeNextIndex(existing);
    const seed: Package = {
      _meta: { id, index: nextIndex },
      slug: '',
      name: '',
      isActive: true,
      isFeatured: false,
    };
    this.openEditor(seed, true);
  }

  onEditPackage(event: Event, pkg: Package): void {
    this.openEditor(_.cloneDeep(pkg), false);
  }

  onDeletePackage(event: Event, pkg: Package): void {
    const modal = this.modalService.generic({
      title: 'Remove Package',
      copy: [
        `Are you sure you want to remove "${pkg.name || pkg._meta.id}"?`,
        'The package will stop appearing in new arrangements and on the public Picaluna site, but its URL will continue to render a "no longer available" notice so existing links don\'t 404.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason?.toString().toLowerCase() === 'confirm') {
          pkg._meta.deleted = true;
          this.persist();
        }
      },
    });
  }

  onRestorePackage(event: Event, pkg: Package): void {
    pkg._meta.deleted = false;
    this.persist();
  }

  /**
   * Open the dedicated editor modal. On Save, the editor mutates the
   * passed-in object (Angular reactive form ↔ object bridge), so we just
   * re-persist account.data afterwards.
   */
  private openEditor(pkg: Package, isNew: boolean): void {
    const ref = this.dialogService.open(AccountPackageComponent, {
      header: isNew ? 'Add package' : `Edit ${pkg.name || 'package'}`,
      width: '90vw',
      baseZIndex: 11000, // above the outer list modal
      data: { pkg, isNew },
    });

    ref.onClose.subscribe({
      next: (result: { saved: boolean; pkg: Package } | undefined) => {
        if (!result?.saved) return;
        const account = this.lastAccountSnapshot();
        if (!account) return;

        // CRITICAL: account.data on the client side is the post-objectsToArrays
        // shape — id-keyed collections like packages are arrays here, not
        // objects. accountService.patch() inverts them back to id-keyed
        // objects before sending. So we MUST do array operations here, or
        // the new package gets dropped during arraysToObjects.
        if (!Array.isArray(account.data.packages)) {
          account.data.packages = [];
        }
        const existingIndex = (account.data.packages as Package[]).findIndex(
          (p) => p?._meta?.id === result.pkg._meta.id
        );
        if (existingIndex >= 0) {
          (account.data.packages as Package[])[existingIndex] = result.pkg;
        } else {
          (account.data.packages as Package[]).push(result.pkg);
        }
        this.persist();
      },
    });
  }

  private persist(): void {
    this.siteService.addSubscriptionLog(
      this,
      'account-package-list.component.ts->persist->this.accountService.account$'
    );

    this.accountService.account$
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'account-package-list.component.ts->persist->this.accountService.account$'
          )
        ),
        takeUntil(this.unsubscribe$),
        first(),
        switchMap((account) => this.accountService.patch(account.data))
      )
      .subscribe({
        next: () => {
          // PATCH succeeded; account stream updates downstream subscribers.
        },
      });
  }

  /** Snapshot the current account so we can mutate it synchronously. */
  private lastAccountSnapshot(): Account | undefined {
    let snapshot: Account | undefined;
    this.accountService.account$.pipe(first()).subscribe({
      next: (a) => (snapshot = a),
    });
    return snapshot;
  }

  /**
   * Pick the next integer index for a new package. Convention from
   * elsewhere in Conductor: gaps of 10 so drag-reorder insertions don't
   * trigger a mass reindex.
   */
  private computeNextIndex(existing: Package[]): number {
    const max = existing.reduce((acc, p) => {
      const i = p?._meta?.index;
      return typeof i === 'number' && i > acc ? i : acc;
    }, -10);
    return max + 10;
  }

  /** Short, URL-safe id suffix — random 8 chars. */
  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }
}
